import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Container, Nav, Dropdown, Icon } from 'rsuite';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { hasViewAccess, hasModifyAccess } from '../../../helpers/permission';
import MainLayout from '../../../containers/MainLayout';
import MainContent from '../../MainContent';
import SidebarNav from '../../SidebarNav';
import NavLink from '../../NavLink';
import DropdownItemLink from '../../DropdownItemLink';
import ItemSkuLookup from '../ItemSkuLookup';
import triggerDownload from '../../../helpers/triggerDownload';
import {
  inventoryCreateItemPath,
  inventoryCreatePackagePath,
  inventoryItemsPath,
  inventoryPackagesPath,
  inventoryTagsPath,
  inventoryLocationsPath,
  inventoryItemsAvailabilityPath,
  inventoryPackagesAvailabilityPath,
  inventoryItemsAuditLogsPath,
  inventoryGuestSummaryReportPath,
  inventoryDonorSummaryReportPath,
  inventoryLowStockItemsPath,
  inventoryOnHandPath,
  inventoryItemsAndPackagesSkusPath,
  inventoryItemsImportPath,
  inventoryItemsQauntityImportPath
} from '../../../routes/Paths';
import {
  getSidebarNavExpanded,
  getDropdownNavOpenKeys
} from '../../../redux/Inventory/selectors/components/page-layout';
import {
  toggleSidebarNav,
  toggleDropdownNavItem,
  resetDropdownNavItems,
  clearItemsTemplateDownloadUrl
} from '../../../redux/Inventory/actions';
import NAV_KEYS, { navKeysByPath } from './nav-keys';
import DownloadItemsFormat from '../DownloadItemsFormat';

const InventoryPageLayout = ({
  children,
  onShowAddItemSkuModal,
  onDownloadInventoryTemplate,
  itemsTemplateDownloadUrl,
  isFetchingItemsTemplateDownloadUrl
}) => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const permissions = useSelector(getUserPermissionsAsMap);
  const [activeKey, setActiveKey] = useState(NAV_KEYS.ITEMS_LIST);
  const [isDownloadingItemsTemplate, setIsDownloadingItemsTemplate] = useState(
    false
  );
  const sidebarExpanded = useSelector(getSidebarNavExpanded);
  const navOpenKeys = useSelector(getDropdownNavOpenKeys);

  useEffect(() => {
    if (!isFetchingItemsTemplateDownloadUrl && itemsTemplateDownloadUrl) {
      triggerDownload(itemsTemplateDownloadUrl);
      dispatch(clearItemsTemplateDownloadUrl());
    }
  });
  useEffect(
    () => {
      const navKey = get(path, navKeysByPath);
      setActiveKey(navKey);
    },
    [path]
  );

  const handleToggleSidebarNav = () => {
    dispatch(toggleSidebarNav());
    if (sidebarExpanded) {
      dispatch(resetDropdownNavItems());
    }
  };

  const handleToggleDropdownNav = navItem => {
    dispatch(toggleDropdownNavItem(navItem));
  };

  const handleDownloadItemsTemplate = () => {
    setIsDownloadingItemsTemplate(true);
  };

  return (
    <MainLayout>
      <ItemSkuLookup />
      <MainContent>
        <SidebarNav
          activeKey={activeKey}
          expanded={sidebarExpanded}
          onToggle={handleToggleSidebarNav}
          openKeys={navOpenKeys}
        >
          {hasModifyAccess(PERMISSIONS.SKU, permissions) && (
            <Nav.Item
              onClick={onShowAddItemSkuModal}
              eventKey={NAV_KEYS.ADD_SKU}
              icon={<Icon icon="barcode" />}
            >
              Add Item/Package SKU
            </Nav.Item>
          )}

          {hasViewAccess(PERMISSIONS.ITEM, permissions) && (
            <Dropdown
              title="Items"
              icon={<Icon icon="list-ul" />}
              eventKey={NAV_KEYS.ITEMS}
              placement="rightStart"
              onClick={() => {
                handleToggleDropdownNav(NAV_KEYS.ITEMS);
              }}
            >
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <DropdownItemLink
                  to={inventoryCreateItemPath}
                  eventKey={NAV_KEYS.ITEMS_CREATE}
                >
                  <Icon icon="file" /> New
                </DropdownItemLink>
              )}
              <DropdownItemLink
                to={inventoryItemsPath}
                eventKey={NAV_KEYS.ITEMS_LIST}
              >
                <Icon icon="list-alt" /> List
              </DropdownItemLink>
              <DropdownItemLink
                to={inventoryItemsAvailabilityPath}
                eventKey={NAV_KEYS.ITEMS_AVAILABILITY}
              >
                <Icon icon="task" /> Availability
              </DropdownItemLink>
              {hasViewAccess(PERMISSIONS.AUDIT, permissions) && (
                <DropdownItemLink
                  to={inventoryItemsAuditLogsPath}
                  eventKey={NAV_KEYS.ITEMS_AUDIT_LOGS}
                >
                  <Icon icon="history" /> Audit Logs
                </DropdownItemLink>
              )}
            </Dropdown>
          )}
          {hasViewAccess(PERMISSIONS.PACKAGE, permissions) && (
            <Dropdown
              title="Packages"
              icon={<Icon icon="archive" />}
              eventKey={NAV_KEYS.PACKAGES}
              placement="rightStart"
              onClick={() => {
                handleToggleDropdownNav(NAV_KEYS.PACKAGES);
              }}
            >
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <DropdownItemLink
                  to={inventoryCreatePackagePath}
                  eventKey={NAV_KEYS.PACKAGES_CREATE}
                >
                  <Icon icon="file" /> New
                </DropdownItemLink>
              )}
              <DropdownItemLink
                to={inventoryPackagesPath}
                eventKey={NAV_KEYS.PACKAGES_LIST}
              >
                <Icon icon="list-alt" /> List
              </DropdownItemLink>
              <DropdownItemLink
                to={inventoryPackagesAvailabilityPath}
                eventKey={NAV_KEYS.PACKAGES_AVAILABILITY}
              >
                <Icon icon="task" /> Availability
              </DropdownItemLink>
            </Dropdown>
          )}

          {hasViewAccess(PERMISSIONS.TAG, permissions) && (
            <NavLink
              to={inventoryTagsPath}
              eventKey={NAV_KEYS.TAGS}
              icon={<Icon icon="tags" />}
            >
              Tags
            </NavLink>
          )}

          {hasViewAccess(PERMISSIONS.LOCATION, permissions) && (
            <NavLink
              to={inventoryLocationsPath}
              eventKey={NAV_KEYS.LOCATIONS}
              icon={<Icon icon="map-marker" />}
            >
              Locations
            </NavLink>
          )}
          <Dropdown
            eventKey={NAV_KEYS.REPORTS}
            title="Reports"
            icon={<Icon icon="bar-chart" />}
            placement="rightStart"
            onClick={() => {
              handleToggleDropdownNav(NAV_KEYS.REPORTS);
            }}
          >
            <DropdownItemLink
              to={inventoryGuestSummaryReportPath}
              icon={<Icon icon="user-analysis" />}
              eventKey={NAV_KEYS.REPORTS_GUEST_SUMMARY}
            >
              Guest Summary
            </DropdownItemLink>
            <DropdownItemLink
              to={inventoryDonorSummaryReportPath}
              icon={<Icon icon="handshake-o" />}
              eventKey={NAV_KEYS.REPORTS_DONOR_SUMMARY}
            >
              Donor Summary
            </DropdownItemLink>
            <DropdownItemLink
              to={inventoryLowStockItemsPath}
              icon={<Icon icon="cart-arrow-down" />}
              eventKey={NAV_KEYS.LOW_STOCK_ITEMS}
            >
              Low Stock Items
            </DropdownItemLink>
            <DropdownItemLink
              to={inventoryOnHandPath}
              icon={<Icon icon="task" />}
              eventKey={NAV_KEYS.ON_HAND}
            >
              On Hand Report
            </DropdownItemLink>
            <DropdownItemLink
              to={inventoryItemsAndPackagesSkusPath}
              icon={<Icon icon="barcode" />}
              eventKey={NAV_KEYS.ITEMS_SKUS}
            >
              Items/Packages SKUs
            </DropdownItemLink>
          </Dropdown>
          {hasViewAccess(PERMISSIONS.ITEM, permissions) && (
            <Dropdown
              title="Imports"
              icon={<Icon icon="import" />}
              eventKey={NAV_KEYS.IMPORT_NAV}
              placement="rightStart"
              onClick={() => {
                handleToggleDropdownNav(NAV_KEYS.IMPORT_NAV);
              }}
            >
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <Dropdown.Item
                  onClick={handleDownloadItemsTemplate}
                  disabled={isDownloadingItemsTemplate}
                  eventKey={`download-${NAV_KEYS.ITEMS_IMPORT}`}
                  componentClass={DownloadItemsFormat}
                >
                  <Icon icon="download" />
                  Get Items Format
                </Dropdown.Item>
              )}
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <Dropdown.Item
                  disabled={isFetchingItemsTemplateDownloadUrl}
                  onClick={onDownloadInventoryTemplate}
                  eventKey={`download-${NAV_KEYS.ITEMS_QUANTITY_IMPORT}`}
                >
                  <Icon icon="download" />
                  {isFetchingItemsTemplateDownloadUrl
                    ? 'Downloading...'
                    : 'Get Quantity Format'}
                </Dropdown.Item>
              )}
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <DropdownItemLink
                  to={inventoryItemsImportPath}
                  eventKey={NAV_KEYS.ITEMS_IMPORT}
                >
                  <Icon icon="import" /> Import Items
                </DropdownItemLink>
              )}
              {hasModifyAccess(PERMISSIONS.ITEM, permissions) && (
                <DropdownItemLink
                  to={inventoryItemsQauntityImportPath}
                  eventKey={NAV_KEYS.ITEMS_QUANTITY_IMPORT}
                >
                  <Icon icon="import" /> Import Quantity
                </DropdownItemLink>
              )}
            </Dropdown>
          )}
        </SidebarNav>
        <Container>{children}</Container>
      </MainContent>
    </MainLayout>
  );
};

InventoryPageLayout.propTypes = {
  children: PropTypes.node.isRequired,
  itemsTemplateDownloadUrl: PropTypes.string,
  isFetchingItemsTemplateDownloadUrl: PropTypes.bool,
  onDownloadInventoryTemplate: PropTypes.func.isRequired,
  onShowAddItemSkuModal: PropTypes.func.isRequired
};

InventoryPageLayout.defaultProps = {
  itemsTemplateDownloadUrl: '',
  isFetchingItemsTemplateDownloadUrl: false
};

export default InventoryPageLayout;
